import React, { useState } from 'react';
import { useTheme } from '@mui/material/styles';
import {
    Box,
    Card,
    CardContent,
    Grid,
    Typography,
    Button,
    IconButton,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Alert,
    LinearProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Tooltip,
    Avatar,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Switch,
    FormControlLabel,
    Tab,
    Tabs,
    Badge
} from '@mui/material';
import ReactApexChart from 'react-apexcharts';

// icons
import {
    IconCpu,
    IconCode,
    IconPlayerStop,
    IconPlayerPause,
    IconRefresh,
    IconSettings,
    IconCheck,
    IconX,
    IconClock,
    IconBolt,
    IconTerminal,
    IconDownload
} from '@tabler/icons-react';

const KernelManagement = () => {
    const theme = useTheme();
    const [tabValue, setTabValue] = useState(0);
    const [selectedKernel, setSelectedKernel] = useState(null);
    const [installDialog, setInstallDialog] = useState(false);
    const [configDialog, setConfigDialog] = useState(false);

    // Mock running kernels
    const runningKernels = [
        {
            id: 'kernel-1',
            name: 'Python 3',
            notebook: 'Data Analysis Tutorial.ipynb',
            status: 'busy',
            startTime: '2024-01-15 10:30:00',
            runtime: '2h 15m',
            cpu: 45,
            memory: 1.2,
            executions: 234,
            lastActivity: '2 mins ago'
        },
        {
            id: 'kernel-2',
            name: 'Python 3 (PyTorch)',
            notebook: 'Machine Learning Model.ipynb',
            status: 'idle',
            startTime: '2024-01-15 09:15:00',
            runtime: '3h 30m',
            cpu: 12,
            memory: 3.5,
            executions: 567,
            lastActivity: '15 mins ago'
        },
        {
            id: 'kernel-3',
            name: 'R',
            notebook: 'Statistical Analysis.ipynb',
            status: 'busy',
            startTime: '2024-01-15 11:45:00',
            runtime: '30m',
            cpu: 78,
            memory: 2.1,
            executions: 89,
            lastActivity: 'Just now'
        }
    ];

    // Available kernels
    const availableKernels = [
        {
            name: 'Python 3',
            version: '3.9.12',
            language: 'python',
            icon: '🐍',
            installed: true,
            description: 'Default Python kernel with standard libraries',
            packages: ['numpy', 'pandas', 'matplotlib', 'scipy', 'sklearn']
        },
        {
            name: 'Python 3 (PyTorch)',
            version: '3.9.12',
            language: 'python',
            icon: '🔥',
            installed: true,
            description: 'Python kernel with PyTorch for deep learning',
            packages: ['torch', 'torchvision', 'torchaudio', 'tensorboard']
        },
        {
            name: 'Python 3 (TensorFlow)',
            version: '3.9.12',
            language: 'python',
            icon: '🧠',
            installed: false,
            description: 'Python kernel with TensorFlow for machine learning',
            packages: ['tensorflow', 'keras', 'tensorboard']
        },
        {
            name: 'R',
            version: '4.2.0',
            language: 'r',
            icon: '📊',
            installed: true,
            description: 'R kernel for statistical computing',
            packages: ['ggplot2', 'dplyr', 'tidyr', 'caret']
        },
        {
            name: 'Julia',
            version: '1.8.0',
            language: 'julia',
            icon: '🔴',
            installed: false,
            description: 'Julia kernel for high-performance computing',
            packages: ['DataFrames', 'Plots', 'Flux', 'DifferentialEquations']
        }
    ];

    // Resource usage chart
    const resourceChartOptions = {
        chart: {
            type: 'line',
            height: 250,
            toolbar: { show: false },
            animations: {
                enabled: true,
                easing: 'linear',
                dynamicAnimation: {
                    speed: 1000
                }
            }
        },
        stroke: {
            curve: 'smooth',
            width: 2
        },
        xaxis: {
            categories: ['10:00', '10:30', '11:00', '11:30', '12:00', '12:30'],
            labels: { style: { colors: theme.palette.text.secondary } }
        },
        yaxis: [
            {
                title: { text: 'CPU %' },
                max: 100,
                labels: { style: { colors: theme.palette.text.secondary } }
            },
            {
                opposite: true,
                title: { text: 'Memory (GB)' },
                max: 8,
                labels: { style: { colors: theme.palette.text.secondary } }
            }
        ],
        colors: [theme.palette.primary.main, theme.palette.secondary.main],
        legend: {
            position: 'top',
            horizontalAlign: 'right'
        }
    };

    const resourceChartSeries = [
        {
            name: 'CPU Usage',
            data: [30, 45, 35, 50, 49, 60]
        },
        {
            name: 'Memory Usage',
            data: [2.1, 3.2, 2.8, 3.5, 3.3, 4.1]
        }
    ];

    const handleKernelAction = (action, kernel) => {
        console.log(`${action} kernel:`, kernel);
    };

    const handleInstallKernel = (kernel) => {
        setSelectedKernel(kernel);
        setInstallDialog(true);
    };

    return (
        <Box>
            <Card>
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
                        <Tab
                            label={
                                <Box display="flex" alignItems="center" gap={1}>
                                    Running Kernels
                                    <Badge badgeContent={runningKernels.length} color="primary" />
                                </Box>
                            }
                        />
                        <Tab label="Available Kernels" />
                        <Tab label="Resource Usage" />
                        <Tab label="Configuration" />
                    </Tabs>
                </Box>

                <CardContent>
                    {/* Running Kernels Tab */}
                    {tabValue === 0 && (
                        <Box>
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                                <Typography variant="h5">Active Kernel Sessions</Typography>
                                <Box display="flex" gap={1}>
                                    <Button variant="outlined" startIcon={<IconPlayerStop />} color="error" size="small">
                                        Shutdown All
                                    </Button>
                                    <Button variant="outlined" startIcon={<IconRefresh />} size="small">
                                        Refresh
                                    </Button>
                                </Box>
                            </Box>

                            <TableContainer component={Paper} variant="outlined">
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Kernel</TableCell>
                                            <TableCell>Notebook</TableCell>
                                            <TableCell>Status</TableCell>
                                            <TableCell>Runtime</TableCell>
                                            <TableCell>CPU</TableCell>
                                            <TableCell>Memory</TableCell>
                                            <TableCell>Activity</TableCell>
                                            <TableCell align="right">Actions</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {runningKernels.map((kernel) => (
                                            <TableRow key={kernel.id}>
                                                <TableCell>
                                                    <Box display="flex" alignItems="center" gap={1}>
                                                        <Avatar sx={{ width: 32, height: 32, bgcolor: theme.palette.primary.light }}>
                                                            <IconCode size={16} />
                                                        </Avatar>
                                                        <Typography variant="body2">{kernel.name}</Typography>
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                                                        {kernel.notebook}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={kernel.status}
                                                        size="small"
                                                        color={kernel.status === 'busy' ? 'warning' : 'default'}
                                                        icon={kernel.status === 'busy' ? <IconBolt size={14} /> : <IconClock size={14} />}
                                                    />
                                                </TableCell>
                                                <TableCell>{kernel.runtime}</TableCell>
                                                <TableCell>
                                                    <Box display="flex" alignItems="center" gap={1}>
                                                        <LinearProgress
                                                            variant="determinate"
                                                            value={kernel.cpu}
                                                            sx={{ width: 50 }}
                                                            color={kernel.cpu > 70 ? 'error' : 'primary'}
                                                        />
                                                        <Typography variant="caption">{kernel.cpu}%</Typography>
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    <Box display="flex" alignItems="center" gap={1}>
                                                        <LinearProgress
                                                            variant="determinate"
                                                            value={(kernel.memory / 8) * 100}
                                                            sx={{ width: 50 }}
                                                            color={kernel.memory > 6 ? 'error' : 'secondary'}
                                                        />
                                                        <Typography variant="caption">{kernel.memory} GB</Typography>
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="caption" color="textSecondary">
                                                        {kernel.lastActivity}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Tooltip title="Interrupt">
                                                        <IconButton size="small" onClick={() => handleKernelAction('interrupt', kernel)}>
                                                            <IconPlayerPause size={16} />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Restart">
                                                        <IconButton size="small" onClick={() => handleKernelAction('restart', kernel)}>
                                                            <IconRefresh size={16} />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Shutdown">
                                                        <IconButton
                                                            size="small"
                                                            color="error"
                                                            onClick={() => handleKernelAction('shutdown', kernel)}
                                                        >
                                                            <IconPlayerStop size={16} />
                                                        </IconButton>
                                                    </Tooltip>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            <Alert severity="info" sx={{ mt: 3 }}>
                                <Typography variant="body2">
                                    <strong>Tip:</strong> Idle kernels consume resources. Consider shutting down kernels that haven't been
                                    active for more than 30 minutes.
                                </Typography>
                            </Alert>
                        </Box>
                    )}

                    {/* Available Kernels Tab */}
                    {tabValue === 1 && (
                        <Box>
                            <Typography variant="h5" gutterBottom>
                                Kernel Specifications
                            </Typography>
                            <Grid container spacing={3}>
                                {availableKernels.map((kernel) => (
                                    <Grid item xs={12} md={6} key={kernel.name}>
                                        <Card variant="outlined">
                                            <CardContent>
                                                <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                                                    <Box display="flex" gap={2}>
                                                        <Typography variant="h2">{kernel.icon}</Typography>
                                                        <Box>
                                                            <Typography variant="h6">{kernel.name}</Typography>
                                                            <Typography variant="body2" color="textSecondary">
                                                                Version {kernel.version}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                    <Chip
                                                        label={kernel.installed ? 'Installed' : 'Not Installed'}
                                                        size="small"
                                                        color={kernel.installed ? 'success' : 'default'}
                                                        icon={kernel.installed ? <IconCheck size={14} /> : <IconX size={14} />}
                                                    />
                                                </Box>

                                                <Typography variant="body2" color="textSecondary" sx={{ my: 2 }}>
                                                    {kernel.description}
                                                </Typography>

                                                <Box mb={2}>
                                                    <Typography variant="subtitle2" gutterBottom>
                                                        Included Packages:
                                                    </Typography>
                                                    <Box display="flex" flexWrap="wrap" gap={0.5}>
                                                        {kernel.packages.map((pkg) => (
                                                            <Chip key={pkg} label={pkg} size="small" />
                                                        ))}
                                                    </Box>
                                                </Box>

                                                <Box display="flex" gap={1}>
                                                    {kernel.installed ? (
                                                        <>
                                                            <Button size="small" startIcon={<IconTerminal />}>
                                                                Open Terminal
                                                            </Button>
                                                            <Button size="small" startIcon={<IconSettings />}>
                                                                Configure
                                                            </Button>
                                                        </>
                                                    ) : (
                                                        <Button
                                                            variant="contained"
                                                            size="small"
                                                            startIcon={<IconDownload />}
                                                            onClick={() => handleInstallKernel(kernel)}
                                                        >
                                                            Install Kernel
                                                        </Button>
                                                    )}
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                ))}
                            </Grid>
                        </Box>
                    )}

                    {/* Resource Usage Tab */}
                    {tabValue === 2 && (
                        <Box>
                            <Grid container spacing={3}>
                                <Grid item xs={12}>
                                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                                        <Typography variant="h5">System Resource Usage</Typography>
                                        <FormControl size="small" sx={{ minWidth: 120 }}>
                                            <InputLabel>Time Range</InputLabel>
                                            <Select defaultValue="1h" label="Time Range">
                                                <MenuItem value="1h">Last Hour</MenuItem>
                                                <MenuItem value="6h">Last 6 Hours</MenuItem>
                                                <MenuItem value="24h">Last 24 Hours</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Box>
                                </Grid>

                                <Grid item xs={12} md={8}>
                                    <Card variant="outlined">
                                        <CardContent>
                                            <Typography variant="h6" gutterBottom>
                                                CPU & Memory Usage
                                            </Typography>
                                            <ReactApexChart
                                                options={resourceChartOptions}
                                                series={resourceChartSeries}
                                                type="line"
                                                height={250}
                                            />
                                        </CardContent>
                                    </Card>
                                </Grid>

                                <Grid item xs={12} md={4}>
                                    <Card variant="outlined" sx={{ mb: 2 }}>
                                        <CardContent>
                                            <Typography variant="h6" gutterBottom>
                                                Current Usage
                                            </Typography>
                                            <List>
                                                <ListItem>
                                                    <ListItemIcon>
                                                        <IconCpu />
                                                    </ListItemIcon>
                                                    <ListItemText
                                                        primary="CPU Usage"
                                                        secondary={
                                                            <Box display="flex" alignItems="center" gap={1}>
                                                                <LinearProgress variant="determinate" value={55} sx={{ flexGrow: 1 }} />
                                                                <Typography variant="caption">55%</Typography>
                                                            </Box>
                                                        }
                                                    />
                                                </ListItem>
                                                <ListItem>
                                                    <ListItemIcon>
                                                        <IconCpu />
                                                    </ListItemIcon>
                                                    <ListItemText
                                                        primary="Memory Usage"
                                                        secondary={
                                                            <Box display="flex" alignItems="center" gap={1}>
                                                                <LinearProgress
                                                                    variant="determinate"
                                                                    value={68}
                                                                    sx={{ flexGrow: 1 }}
                                                                    color="secondary"
                                                                />
                                                                <Typography variant="caption">5.4/8 GB</Typography>
                                                            </Box>
                                                        }
                                                    />
                                                </ListItem>
                                            </List>
                                        </CardContent>
                                    </Card>

                                    <Card variant="outlined">
                                        <CardContent>
                                            <Typography variant="h6" gutterBottom>
                                                Kernel Statistics
                                            </Typography>
                                            <List dense>
                                                <ListItem>
                                                    <ListItemText primary="Active Kernels" />
                                                    <Typography variant="body2">{runningKernels.length}</Typography>
                                                </ListItem>
                                                <ListItem>
                                                    <ListItemText primary="Total Executions" />
                                                    <Typography variant="body2">890</Typography>
                                                </ListItem>
                                                <ListItem>
                                                    <ListItemText primary="Avg. Execution Time" />
                                                    <Typography variant="body2">1.2s</Typography>
                                                </ListItem>
                                                <ListItem>
                                                    <ListItemText primary="Failed Executions" />
                                                    <Typography variant="body2" color="error">
                                                        12
                                                    </Typography>
                                                </ListItem>
                                            </List>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            </Grid>
                        </Box>
                    )}

                    {/* Configuration Tab */}
                    {tabValue === 3 && (
                        <Box>
                            <Typography variant="h5" gutterBottom>
                                Kernel Configuration
                            </Typography>
                            <Grid container spacing={3}>
                                <Grid item xs={12} md={6}>
                                    <Card variant="outlined">
                                        <CardContent>
                                            <Typography variant="h6" gutterBottom>
                                                Resource Limits
                                            </Typography>
                                            <TextField
                                                fullWidth
                                                label="CPU Limit per Kernel"
                                                defaultValue="2"
                                                type="number"
                                                sx={{ mb: 2 }}
                                                InputProps={{
                                                    endAdornment: 'cores'
                                                }}
                                            />
                                            <TextField
                                                fullWidth
                                                label="Memory Limit per Kernel"
                                                defaultValue="4"
                                                type="number"
                                                sx={{ mb: 2 }}
                                                InputProps={{
                                                    endAdornment: 'GB'
                                                }}
                                            />
                                            <TextField
                                                fullWidth
                                                label="Max Execution Time"
                                                defaultValue="3600"
                                                type="number"
                                                InputProps={{
                                                    endAdornment: 'seconds'
                                                }}
                                            />
                                        </CardContent>
                                    </Card>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Card variant="outlined">
                                        <CardContent>
                                            <Typography variant="h6" gutterBottom>
                                                Auto-shutdown Settings
                                            </Typography>
                                            <FormControlLabel
                                                control={<Switch defaultChecked />}
                                                label="Enable auto-shutdown for idle kernels"
                                                sx={{ mb: 2 }}
                                            />
                                            <TextField
                                                fullWidth
                                                label="Idle Timeout"
                                                defaultValue="30"
                                                type="number"
                                                sx={{ mb: 2 }}
                                                InputProps={{
                                                    endAdornment: 'minutes'
                                                }}
                                            />
                                            <FormControlLabel control={<Switch defaultChecked />} label="Shutdown kernels on low memory" />
                                        </CardContent>
                                    </Card>
                                </Grid>
                                <Grid item xs={12}>
                                    <Card variant="outlined">
                                        <CardContent>
                                            <Typography variant="h6" gutterBottom>
                                                Default Packages
                                            </Typography>
                                            <Alert severity="info" sx={{ mb: 2 }}>
                                                These packages will be automatically installed in all new Python kernels
                                            </Alert>
                                            <TextField
                                                fullWidth
                                                multiline
                                                rows={4}
                                                defaultValue="numpy==1.23.0&#10;pandas==1.4.3&#10;matplotlib==3.5.2&#10;scipy==1.8.1&#10;scikit-learn==1.1.1"
                                                label="Requirements.txt"
                                            />
                                        </CardContent>
                                    </Card>
                                </Grid>
                            </Grid>
                            <Box display="flex" justifyContent="flex-end" mt={3}>
                                <Button variant="contained" startIcon={<IconCheck />}>
                                    Save Configuration
                                </Button>
                            </Box>
                        </Box>
                    )}
                </CardContent>
            </Card>

            {/* Install Kernel Dialog */}
            <Dialog open={installDialog} onClose={() => setInstallDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Install {selectedKernel?.name}</DialogTitle>
                <DialogContent>
                    <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
                        This will install the kernel and all required dependencies. The process may take several minutes.
                    </Alert>
                    <Typography variant="subtitle2" gutterBottom>
                        The following packages will be installed:
                    </Typography>
                    <Box display="flex" flexWrap="wrap" gap={0.5} mb={2}>
                        {selectedKernel?.packages.map((pkg) => (
                            <Chip key={pkg} label={pkg} size="small" />
                        ))}
                    </Box>
                    <LinearProgress variant="determinate" value={0} />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setInstallDialog(false)}>Cancel</Button>
                    <Button variant="contained" startIcon={<IconDownload />}>
                        Install Kernel
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default KernelManagement;
